import {
  Dimension,
  MultiStepDef,
  Padding,
  PerSideBorder,
} from "@superblocksteam/shared";
import { clamp } from "lodash";
import styled, { css } from "styled-components";
import tinycolor from "tinycolor2";
import { CanvasLayout, WIDGET_PADDING } from "legacy/constants/WidgetConstants";
import { GeneratedTheme, TextStyleWithVariant } from "legacy/themes";
import { INPUT_PADDING } from "legacy/themes/constants";
import {
  DEFAULT_INPUT_TEXT_STYLE_VARIANT,
  DEFAULT_TYPOGRAPHIES_NEW,
} from "legacy/themes/typographyConstants";
import { isFitContent } from "./base/sizing";
import {
  getFontSizeInPxFromTextStyle,
  getIconSizeBasedOnFontSize,
} from "./typographyUtils";
import type {
  DynamicLayoutProps,
  DynamicVisibilityProperties,
  WidgetProps,
  WidgetPropsRuntime,
} from "./BaseWidget";
import type {
  TabContainerWidgetProps,
  TabsWidgetProps,
} from "./TabsWidget/types";
import type { FlattenedWidgetProps } from "legacy/reducers/entityReducers/canvasWidgetsReducer";

export const WIDGET_STATIC_PROPS = {
  widgetId: true,
  widgetName: true,
  type: true,

  children: true,
  parentId: true,
  parentColumnSpace: true,
  parentRowSpace: true,
  internalWidth: true,
  internalMaxWidth: true,

  left: true,
  top: true,
  width: true,
  minWidth: true,
  maxWidth: true,
  height: true,
  minHeight: true,
  maxHeight: true,

  // CANVAS
  gridColumns: true,
  layout: true,
  spacing: true,
  alignment: true,
  distribution: true,

  canExtend: true,
  detachFromLayout: true,
  padding: true,
  margin: true,
  openParentPropertyPane: true,
  appMode: true,
  shouldScrollContents: true,
  // CONTAINER
  containerStyle: true,
  // GRID
  columnCount: true,
  gridCellScrollable: true,
  // SLIDEOUT
  size: true,
  // MODAL
  heightPreset: true,
  widthPreset: true,
  // TABS
  shouldShowTabs: true,
  // Section
  stackColumnsAt: true,
  isStacked: true,
  position: true,
  stickyBehavior: true,
};
export const STATIC_KEYS = Object.keys(WIDGET_STATIC_PROPS);

// We want these to be available in recalculateWidgetsLayout in size.ts, but want to make sure the evaluated version can pass through
// This is an ugly solution. If there is another way, pls bug alex so he can fix it.
const SEMI_STATIC_WIDGET_PROPS = {
  headerProps: true,
  border: true,
};
const SEMI_STATIC_KEYS = Object.keys(SEMI_STATIC_WIDGET_PROPS);

export interface StaticWidgetProps<CHILD = unknown>
  extends Pick<
    WidgetPropsRuntime & {
      columnCount?: number; // GRID
      gridCellScrollable?: boolean; // GRID
      size?: any; // MODAL
      gridColumns?: number; // CANVAS
      layout?: CanvasLayout; // CANVAS
      spacing?: Dimension<"px">; // CANVAS

      containerStyle?: "card" | "none"; // CANVAS & CONTAINER & FORM

      shouldShowTabs?: TabsWidgetProps<TabContainerWidgetProps>["shouldShowTabs"]; // TABS
      stackColumnsAt?: Dimension<"px">; // SECTION
      isStacked?: boolean; // SECTION
    },
    keyof typeof WIDGET_STATIC_PROPS
  > {
  children?: CHILD[];
  parentLayout?: CanvasLayout; // SECTION
  _semiStaticProps?: {
    headerProps?: TabsWidgetProps<TabContainerWidgetProps>["headerProps"]; // TABS
    border?: FlattenedWidgetProps["border"];
  };
}
// children are all expanded
export type WidgetLayoutProps = StaticWidgetProps<WidgetLayoutProps> &
  DynamicVisibilityProperties &
  DynamicLayoutProps;

// children are stringIds
export type FlattenedWidgetLayoutProps = StaticWidgetProps<string> &
  DynamicVisibilityProperties &
  DynamicLayoutProps;

export type FlattenedWidgetLayoutMap = Record<
  string,
  FlattenedWidgetLayoutProps
>;

/**
 * @param {FlattenedWidgetProps} widget - this is the widget that we're going to be picking the static props from.
 * @returns A new object with the same properties as the input object, but with the dynamic properties removed.
 */
export const pickStaticProps = (
  widget: FlattenedWidgetProps | Partial<Omit<WidgetProps, "children">>,
  includeSemiStaticProps = false,
): Omit<FlattenedWidgetLayoutProps, "children"> => {
  const result: Partial<FlattenedWidgetLayoutProps> = {};
  STATIC_KEYS.forEach((key) => {
    const value = (widget as any)[key];
    if (value !== undefined) (result as any)[key] = value;
  });
  if (includeSemiStaticProps) {
    SEMI_STATIC_KEYS.forEach((key) => {
      const value = (widget as any)[key];
      if (value !== undefined) {
        if (!result._semiStaticProps) {
          result._semiStaticProps = {};
        }
        (result._semiStaticProps as any)[key] = value;
      }
    });
  }
  return result as FlattenedWidgetLayoutProps;
};

/*
  These icons will override the tiny MCE defaults. They are stored here as strings because this is how tinyMCE expects icons to be registered 
  (the alternative is creating a published npm icon library). If you want to replace a tinyMCE icon, just add it here + the chat widget and rich 
  text editor will automatically register it.
*/
export const RICH_TEXT_EDITOR_ICONS: Record<string, string> = {
  image: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M3.57708 1.375H3.6H12.4H12.4229H12.423C12.6833 1.37499 12.9152 1.37498 13.1077 1.3907C13.3131 1.40748 13.528 1.44524 13.7377 1.55212C14.0435 1.70791 14.2921 1.9565 14.4479 2.26227C14.5548 2.47202 14.5925 2.68691 14.6093 2.89231C14.625 3.08478 14.625 3.31668 14.625 3.57707V3.6V12.4V12.4229C14.625 12.6833 14.625 12.9152 14.6093 13.1077C14.5925 13.3131 14.5548 13.528 14.4479 13.7377C14.2921 14.0435 14.0435 14.2921 13.7377 14.4479C13.528 14.5548 13.3131 14.5925 13.1077 14.6093C12.9152 14.625 12.6833 14.625 12.4229 14.625H12.4H3.6H3.57707C3.31668 14.625 3.08478 14.625 2.89231 14.6093C2.68691 14.5925 2.47202 14.5548 2.26227 14.4479C1.9565 14.2921 1.70791 14.0435 1.55212 13.7377C1.44524 13.528 1.40748 13.3131 1.3907 13.1077C1.37498 12.9152 1.37499 12.6833 1.375 12.423V12.4229V12.4V3.6V3.57708V3.57704C1.37499 3.31667 1.37498 3.08478 1.3907 2.89231C1.40748 2.68691 1.44524 2.47202 1.55212 2.26227C1.70791 1.9565 1.9565 1.70791 2.26227 1.55212C2.47202 1.44524 2.68691 1.40748 2.89231 1.3907C3.08478 1.37498 3.31667 1.37499 3.57704 1.375H3.57708ZM2.82984 2.66583L2.82976 2.66587C2.75919 2.70183 2.70183 2.75919 2.66587 2.82976L2.66583 2.82984C2.66353 2.83435 2.64695 2.86686 2.63655 2.9941C2.62549 3.1295 2.625 3.30966 2.625 3.6V11.1578L2.89139 10.8914L4.08119 9.7016L4.09901 9.68378C4.40065 9.38213 4.65348 9.12929 4.87714 8.93941C5.11078 8.74107 5.35338 8.57499 5.64576 8.47999C6.09264 8.33479 6.57401 8.33479 7.02089 8.47999C7.31327 8.57499 7.55587 8.74107 7.78951 8.93941C8.01318 9.12929 8.26601 9.38214 8.56766 9.6838L8.58546 9.7016L9.33366 10.4498C9.53541 10.2498 9.71403 10.0779 9.87714 9.93941C10.1108 9.74107 10.3534 9.57499 10.6458 9.47999C11.0926 9.33479 11.574 9.33479 12.0209 9.47999C12.3133 9.57499 12.5559 9.74107 12.7895 9.93941C12.9639 10.0874 13.1559 10.2737 13.375 10.4915V3.6C13.375 3.30966 13.3745 3.1295 13.3635 2.9941C13.3531 2.86687 13.3365 2.83436 13.3342 2.82984L13.3341 2.82976C13.2982 2.75919 13.2408 2.70183 13.1702 2.66587L13.1702 2.66583C13.1657 2.66353 13.1332 2.64695 13.0059 2.63655C12.8705 2.62549 12.6903 2.625 12.4 2.625H3.6C3.30966 2.625 3.1295 2.62549 2.9941 2.63655C2.86686 2.64695 2.83435 2.66353 2.82984 2.66583ZM13.375 12.2196C13.3209 12.1911 13.2702 12.1541 13.2247 12.1086L12.7016 11.5855C12.3775 11.2614 12.1606 11.0452 11.9805 10.8923C11.806 10.7442 11.7076 10.6925 11.6346 10.6688C11.4388 10.6052 11.2279 10.6052 11.032 10.6688C10.9591 10.6925 10.8606 10.7442 10.6861 10.8923C10.5573 11.0017 10.4098 11.1434 10.2176 11.3337L12.1086 13.2247C12.1541 13.2702 12.1911 13.321 12.2196 13.375H12.4C12.6903 13.375 12.8705 13.3745 13.0059 13.3635C13.1331 13.3531 13.1656 13.3365 13.1702 13.3342L13.1702 13.3341C13.2408 13.2982 13.2982 13.2408 13.3341 13.1702L13.3342 13.1702C13.3365 13.1656 13.3531 13.1331 13.3635 13.0059C13.3745 12.8705 13.375 12.6903 13.375 12.4V12.2196ZM10.4911 13.375L8.89139 11.7753L7.70158 10.5855C7.37747 10.2614 7.16063 10.0452 6.98054 9.89234C6.80603 9.74419 6.70756 9.69251 6.63462 9.66881C6.4388 9.60519 6.22786 9.60519 6.03204 9.66881C5.95909 9.69251 5.86063 9.74419 5.68612 9.89234C5.50602 10.0452 5.28918 10.2614 4.96507 10.5855L3.77527 11.7753L2.77527 12.7753C2.7311 12.8195 2.68196 12.8556 2.62965 12.8838C2.6313 12.9287 2.63353 12.969 2.63655 13.0059C2.64695 13.1332 2.66353 13.1657 2.66583 13.1702L2.66587 13.1702C2.70183 13.2408 2.75919 13.2982 2.82976 13.3341L2.82984 13.3342C2.83436 13.3365 2.86687 13.3531 2.9941 13.3635C3.1295 13.3745 3.30966 13.375 3.6 13.375H10.4911ZM10 5.29165C9.60881 5.29165 9.29168 5.60878 9.29168 5.99999C9.29168 6.39119 9.60881 6.70832 10 6.70832C10.3912 6.70832 10.7083 6.39119 10.7083 5.99999C10.7083 5.60878 10.3912 5.29165 10 5.29165ZM8.04168 5.99999C8.04168 4.91843 8.91845 4.04165 10 4.04165C11.0816 4.04165 11.9583 4.91843 11.9583 5.99999C11.9583 7.08154 11.0816 7.95832 10 7.95832C8.91845 7.95832 8.04168 7.08154 8.04168 5.99999Z" fill="currentColor"/>
  </svg>`,

  link: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="no-fill">
    <path d="M5.88124 10.1187L10.1187 5.875M9.06249 11.1812L7.29374 12.95C7.01509 13.2286 6.68429 13.4497 6.32022 13.6005C5.95614 13.7513 5.56593 13.8289 5.17187 13.8289C4.37601 13.8289 3.61275 13.5128 3.04999 12.95C2.48723 12.3872 2.17108 11.624 2.17108 10.8281C2.17108 10.0323 2.48723 9.26901 3.04999 8.70625L4.81874 6.9375M11.1812 9.06255L12.95 7.2938C13.5127 6.73105 13.8289 5.96778 13.8289 5.17193C13.8289 4.37607 13.5127 3.61281 12.95 3.05005C12.3872 2.4873 11.624 2.17114 10.8281 2.17114C10.0323 2.17114 9.26899 2.4873 8.70623 3.05005L6.93748 4.8188" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,

  bold: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="no-fill">
  <path d="M3 8L10 8C11.6569 8 13 9.34315 13 11V11C13 12.6569 11.6569 14 10 14L3.44776 14C3.20047 14 3 13.7995 3 13.5522L3 8ZM3 8L7.9375 8C9.59435 8 10.9375 6.65685 10.9375 5V5C10.9375 3.34315 9.59435 2 7.9375 2L3.35541 2C3.15912 2 3 2.15912 3 2.35541L3 8Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,

  italic: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M6.07143 2L9.5 2M12.5 2L9.5 2M3.5 14L6.5 14M9.92857 14L6.5 14M9.5 2L6.5 14" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,

  "strike-through": `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="no-fill">
  <path d="M7.25908 8L8.74056 8C10.377 8 11.7035 9.34314 11.7035 11C11.7035 12.6569 10.377 14 8.74056 14L7.25908 14C5.62268 14 4.29612 12.6569 4.29612 11M11.7035 5C11.7035 3.34315 10.377 2 8.74056 2L7.25908 2C5.62268 2 4.29612 3.34315 4.29612 5C4.29612 5.25897 4.32852 5.51028 4.38946 5.75M2 8L14 8" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,

  sourcecode: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="no-fill">
  <path d="M3.33331 5.5L0.833312 8L3.33331 10.5M12.3333 5.5L14.8333 8L12.3333 10.5M9.33331 2L6.33331 14" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,

  "unordered-list": `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M6.5 13.0001L14 13.0001M6.5 8.00002L14 8.00003M6.5 3.00004L14 3.00004M3.11538 3.00004C3.11538 3.17872 2.97763 3.32357 2.80769 3.32357C2.63776 3.32357 2.5 3.17872 2.5 3.00004M3.11538 3.00004C3.11538 2.82136 2.97763 2.67651 2.80769 2.67651C2.63776 2.67651 2.5 2.82136 2.5 3.00004M3.11538 3.00004L2.5 3.00004M3.11538 8.00002C3.11538 8.17871 2.97763 8.32355 2.80769 8.32355C2.63776 8.32355 2.5 8.17871 2.5 8.00002M3.11538 8.00002C3.11538 7.82134 2.97763 7.6765 2.80769 7.6765C2.63776 7.6765 2.5 7.82134 2.5 8.00002M3.11538 8.00002L2.5 8.00002M3.11538 13.0001C3.11538 13.1787 2.97763 13.3236 2.80769 13.3236C2.63776 13.3236 2.5 13.1787 2.5 13.0001M3.11538 13.0001C3.11538 12.8214 2.97763 12.6765 2.80769 12.6765C2.63776 12.6765 2.5 12.8214 2.5 13.0001M3.11538 13.0001L2.5 13.0001" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,

  "ordered-list": `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="no-fill">
  <g clip-path="url(#clip0_3461_89200)">
  <path d="M6.16668 12.9999L14.6668 12.9999M6.00009 8L14.6668 8M6.16668 2.99994L14.6668 2.99994M2.33379 5.49995L2.33342 2L1.00001 2.99994M2.33379 5.49995L1.00034 5.49991M2.33379 5.49995L3.50036 5.49995M1.00001 8.99991L3.49967 8.99991L3.5 10.9999L1.00034 10.9999L1.00017 12.9999L3.49967 12.9999" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
  <clipPath id="clip0_3461_89200">
  <rect width="16" height="16" fill="white"/>
  </clipPath>
  </defs>
  </svg>`,

  emoji: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="no-fill">
  <path d="M6.33334 6.66659C6.33334 6.85068 6.18411 6.99992 6.00001 6.99992C5.81592 6.99992 5.66668 6.85068 5.66668 6.66659M6.33334 6.66659C6.33334 6.48249 6.18411 6.33325 6.00001 6.33325C5.81592 6.33325 5.66668 6.48249 5.66668 6.66659M6.33334 6.66659H5.66668M10.3333 6.66659C10.3333 6.85068 10.1841 6.99992 10 6.99992C9.81592 6.99992 9.66668 6.85068 9.66668 6.66659M10.3333 6.66659C10.3333 6.48249 10.1841 6.33325 10 6.33325C9.81592 6.33325 9.66668 6.48249 9.66668 6.66659M10.3333 6.66659H9.66668M10.6668 9.99992C10.0587 10.8096 9.09047 11.3333 7.99993 11.3333C6.90938 11.3333 5.94116 10.8096 5.33302 9.99992M8.00001 14.6666V14.6666C11.6819 14.6666 14.6667 11.6818 14.6667 7.99992V7.99992C14.6667 4.31802 11.6819 1.33325 8.00001 1.33325V1.33325C4.31811 1.33325 1.33334 4.31802 1.33334 7.99992V7.99992C1.33334 11.6818 4.31811 14.6666 8.00001 14.6666Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,
};

const MIN_LOADER_SIZE = 150;
const LOADING_BG_WIDTH_PERCENT = 0.35;
const getLoadingAnimatorBg = (bgColorHex: string) => {
  const transparentBg = tinycolor(bgColorHex).setAlpha(0).toString();
  return `linear-gradient(
    270deg,
    ${transparentBg} 0%,
    ${bgColorHex} 52.56%,
    ${transparentBg} 100%
  );`;
};
export const LoadingAnimator = styled.div<{ $width: number; bgColor: string }>`
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  width: 100%;
  background: ${(props) => getLoadingAnimatorBg(props.bgColor)};

  background-repeat: no-repeat;
  animation: movingLoader 1500ms infinite;
  animation-timing-function: linear;
  ${({ $width }) => {
    const loadingSizePx = Math.max(
      MIN_LOADER_SIZE,
      $width * LOADING_BG_WIDTH_PERCENT,
    );
    return css`
      background-size: ${loadingSizePx}px 100%;
      background-position: -${loadingSizePx}px 0;
      @keyframes movingLoader {
        to {
          background-position: calc(100% + ${loadingSizePx}px) 0;
        }
      }
    `;
  }}
`;

export const isActionHandlerSet = (actionSteps?: MultiStepDef) => {
  return (
    actionSteps &&
    actionSteps.length > 0 &&
    actionSteps.some((a) => (a as any)?.type)
  );
};

// Spacing between icon and text in Input, Textarea, etc. in pixels
export const DEFAULT_ICON_SPACING = 5;
export const DEFAULT_ICON_SIZE = 18;
const STEPPER_WIDTH = 24;

export const getIconPositionStyle = ({
  padding,
  border,
  defaultBorderWidth = 0,
  iconPosition,
  hasSuffix,
}: {
  padding: Padding;
  border?: PerSideBorder;
  defaultBorderWidth?: number;
  iconPosition?: "LEFT" | "RIGHT";
  hasSuffix?: boolean;
}) => {
  let offsetLeft: string | number = "auto";
  let offsetRight: string | number = "auto";

  if (iconPosition === "LEFT") {
    offsetLeft =
      (padding.left?.value ?? 0) +
      (border?.left?.width?.value ?? defaultBorderWidth);
  } else if (hasSuffix) {
    offsetRight =
      STEPPER_WIDTH +
      (padding.right?.value ?? 0) +
      (border?.right?.width?.value ?? defaultBorderWidth);
  } else {
    offsetRight =
      (padding.right?.value ?? 0) +
      (border?.right?.width?.value ?? defaultBorderWidth);
  }

  return {
    left: offsetLeft === "auto" ? offsetLeft : `${offsetLeft}px`,
    right: offsetRight === "auto" ? offsetRight : `${offsetRight}px`,
  };
};

export const getInputIconSize = ({
  width,
  componentWidth,
  textStyle,
  padding,
  generatedTheme,
}: {
  width?: Dimension;
  componentWidth: number;
  textStyle?: TextStyleWithVariant;
  padding?: Padding;
  generatedTheme: GeneratedTheme;
}) => {
  const generatedInputTheme = generatedTheme.typographies;
  const isAutoWidth = isFitContent(width?.mode);

  return textStyle
    ? getIconSizeBasedOnFontSize(
        getFontSizeInPxFromTextStyle({
          nestedProps: textStyle,
          typographies: generatedInputTheme ?? DEFAULT_TYPOGRAPHIES_NEW,
          textStyleVariant: textStyle.variant,
          defaultTextStyleVariant: DEFAULT_INPUT_TEXT_STYLE_VARIANT,
        }),
      )
    : clamp(
        !isAutoWidth
          ? componentWidth - Padding.x(padding).value - WIDGET_PADDING * 2
          : 18,
        8,
        18,
      );
};

export const getIconPadding = (
  paddingValue: Dimension<"px"> = INPUT_PADDING.right,
  iconSize?: number,
  withSpacing: boolean = true,
) => {
  const spacing = withSpacing ? DEFAULT_ICON_SPACING : 0;
  return Dimension.add(
    Dimension.px(iconSize ? iconSize + spacing : DEFAULT_ICON_SIZE),
    paddingValue,
  ).asFirst();
};
